import { CATEGORY_PUBLICITY } from '../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { ClipboardList } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import FormPage from '../../components/FormPage'
import { FormCols, FormSection, FormSpan, SwitchField, SwitchGroup, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import useGoBack from '../../components/useGoBack'
import { useToast } from '../../components/toastContext'
import { getAssessment, getAssessmentsPage, listAssessments, saveAssessment } from './engagementService'
import { assessmentsStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'

const EMPTY = {
  name: '',
  descriptionAr: '',
  nameEn: '',
  descriptionEn: '',
  startDate: '',
  endDate: '',
  enabled: true,
  mandatory: false,
  questionsCount: '10',
  passLimit: '60',
  publicity: String(CATEGORY_PUBLICITY.PUBLIC),
  categoryIds: [],
  groupIds: [],
  baselineId: '',
}

/**
 * قياس جديد / تعديل — the assessment form, on its own page.
 *
 * It was a side sheet, and the two controls people actually come here for are the topic
 * picker and the department picker: filterable lists over directories that run to hundreds.
 * 560px of column gave each of them a scroll box inside a scroll box. The page gives them
 * room, and the browser's own Back a meaning — see `FormPage`.
 *
 * Three decisions, three steps, which is what the fourteen controls have always been:
 * what the assessment IS, when it runs and how it is graded, and where its questions come
 * from and who sits it.
 *
 * `التخصيص` filters the topic list rather than posting a value of its own — it is the
 * legacy's `drpDnCategoriesFilter`, a view control, not a field. Switching it never
 * silently drops topics already chosen.
 */
export default function AssessmentFormPage() {
  const { id } = useParams()
  const location = useLocation()
  const goBack = useGoBack('/assessments-management')
  const toast = useToast()

  const [values, setValues] = useState(EMPTY)
  /* The other assessments, for the baseline picker — a follow-up names the one it follows. */
  const [others, setOthers] = useState([])
  useEffect(() => {
    const controller = new AbortController()
    listAssessments(controller.signal)
      .then((response) => setOthers(response.rows.filter((row) => String(row.id) !== String(id ?? ''))))
      .catch(() => {})
    return () => controller.abort()
  }, [id])
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true

    /* The topics and groups come from this screen's OWN payload, not the shared engagement
       options — it returns them filtered to what an assessment may draw from. */
    getAssessmentsPage()
      .then((page) => {
        if (!alive || !page) return
        setCategories(page.categories ?? [])
        setGroups(page.groups ?? [])
      })
      .catch(() => {})

    if (!id) {
      return () => {
        alive = false
      }
    }

    /* The row travels with the click, so editing opens filled in with no second request;
       a form reached by its own URL asks the API for its assessment. */
    const seed = (assessment) => {
      if (!alive || !assessment) return
      setValues({
        ...EMPTY,
        ...assessment,
        questionsCount: String(assessment.questionsCount ?? EMPTY.questionsCount),
        passLimit: String(assessment.passLimit ?? EMPTY.passLimit),
        publicity: String(assessment.publicity ?? CATEGORY_PUBLICITY.PUBLIC),
        categoryIds: assessment.categoryIds ?? [],
        groupIds: assessment.groupIds ?? [],
      })
    }

    const carried = location.state?.assessment
    if (carried) seed(carried)
    else getAssessment(id).then(seed).catch(() => {})

    return () => {
      alive = false
    }
    // The carried row is read once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  /* A view filter, not a field: it narrows the topic list and nothing else. */
  const visibleCategories = useMemo(
    () => categories.filter((category) =>
      String(category.publicity ?? CATEGORY_PUBLICITY.PUBLIC) === values.publicity),
    [categories, values.publicity],
  )

  const steps = [
    { id: 'identity', label: S.form.identity },
    { id: 'schedule', label: S.form.schedule },
    { id: 'scope', label: S.form.scope },
  ]

  /** What each step will not let you leave without. */
  const check = (step) => {
    const found = {}
    if (step === 0) {
      if (!values.name.trim()) found.name = S.form.required
      if (!values.nameEn.trim()) found.nameEn = S.form.required
      /* BOTH DESCRIPTIONS, because `SaveAssessmentHandler` refuses without them —
         "جميع الحقول مطلوبة". The drawer left them optional, so the admin filled every step
         and was turned away at the save with a message that named no field. Asked for on the
         step that holds them instead. */
      if (!values.descriptionAr.trim()) found.descriptionAr = S.form.required
      if (!values.descriptionEn.trim()) found.descriptionEn = S.form.required
    }
    if (step === 1) {
      if (!values.startDate) found.startDate = S.form.required
      if (!values.endDate) found.endDate = S.form.required
      if (!String(values.questionsCount).trim()) found.questionsCount = S.form.required
      if (!String(values.passLimit).trim()) found.passLimit = S.form.required

      /* THE SAME THREE RULES THE SAVE APPLIES, asked on the step that holds the fields —
         the pattern already used for the descriptions above. Presence was all this checked,
         so an admin could set an end date before the start, walk through the remaining
         steps, and only then be turned away by the server with the whole form filled in. */
      if (values.startDate && values.endDate && values.endDate < values.startDate) {
        found.endDate = S.form.endBeforeStart
      }
      const questions = Number(values.questionsCount)
      if (!found.questionsCount && (!Number.isInteger(questions) || questions < 2 || questions > 1000)) {
        found.questionsCount = S.form.questionsRange
      }
      const pass = Number(values.passLimit)
      if (!found.passLimit && (!Number.isInteger(pass) || pass < 1 || pass > 100)) {
        found.passLimit = S.form.passRange
      }
    }
    if (step === 2) {
      if (values.categoryIds.length === 0) found.categoryIds = S.form.required
      /* `SaveAssessmentHandler` requires at least one category AND at least one group, so an
         assessment with no audience cannot be saved — it was refused server-side with
         "يجب اختيار فئة ومجموعة واحدة على الأقل" after the form had already been filled in. */
      if (values.groupIds.length === 0) found.groupIds = S.form.required
    }
    return found
  }

  const validateStep = (step) => {
    const found = check(step)
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    const found = { ...check(0), ...check(1), ...check(2) }
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setSaving(true)
    const outcome = await saveAssessment({ ...values, id: id ? Number(id) : undefined })
    setSaving(false)

    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || S.fail.save)
      return
    }
    toast.success(serverText(outcome.message) || S.saved)
    goBack()
  }

  return (
    <FormPage
      icon={ClipboardList}
      title={id ? S.row.update : S.create}
      subtitle={S.title}
      steps={steps}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {({ step }) => {
        if (step === 0) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.nameAr} error={errors.name}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      value={values.name}
                      onChange={set('name')}
                      invalid={!!errors.name}
                      autoComplete="off"
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.nameEn} error={errors.nameEn}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      dir="ltr"
                      value={values.nameEn}
                      onChange={set('nameEn')}
                      invalid={!!errors.nameEn}
                      autoComplete="off"
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.descriptionAr} error={errors.descriptionAr}>
                  {(fieldId) => (
                    <Textarea
                      id={fieldId}
                      rows={4}
                      value={values.descriptionAr}
                      onChange={set('descriptionAr')}
                      invalid={!!errors.descriptionAr}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.descriptionEn} error={errors.descriptionEn}>
                  {(fieldId) => (
                    <Textarea
                      id={fieldId}
                      dir="ltr"
                      rows={4}
                      value={values.descriptionEn}
                      onChange={set('descriptionEn')}
                      invalid={!!errors.descriptionEn}
                    />
                  )}
                </FormRow>
              </FormCols>
            </FormSection>
          )
        }

        if (step === 1) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.startDate} error={errors.startDate}>
                  {(fieldId) => (
                    <DatePicker
                      id={fieldId}
                      value={values.startDate}
                      onChange={set('startDate')}
                      max={values.endDate || undefined}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.endDate} error={errors.endDate}>
                  {(fieldId) => (
                    <DatePicker
                      id={fieldId}
                      value={values.endDate}
                      onChange={set('endDate')}
                      min={values.startDate || undefined}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.questionsCount} error={errors.questionsCount}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      type="number"
                      min="1"
                      dir="ltr"
                      value={values.questionsCount}
                      onChange={set('questionsCount')}
                      invalid={!!errors.questionsCount}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.baseline} hint={S.form.baselineHint}>
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      value={values.baselineId}
                      onChange={set('baselineId')}
                      options={[
                        { value: '', label: S.form.baselineNone },
                        ...others.map((row) => ({ value: String(row.id), label: row.title || row.name || String(row.id) })),
                      ]}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.passLimit} error={errors.passLimit} hint={S.form.passHint}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      type="number"
                      min="0"
                      max="100"
                      dir="ltr"
                      value={values.passLimit}
                      onChange={set('passLimit')}
                      invalid={!!errors.passLimit}
                    />
                  )}
                </FormRow>

                {/* The two states an assessment can be in, with the dates that decide when
                    they apply — not off on a step of their own. */}
                <FormSpan>
                  <SwitchGroup>
                    <SwitchField
                      checked={values.enabled}
                      onChange={set('enabled')}
                      label={S.form.enabled}
                    />
                    <SwitchField
                      checked={values.mandatory}
                      onChange={set('mandatory')}
                      label={S.form.mandatory}
                    />
                  </SwitchGroup>
                </FormSpan>
              </FormCols>
            </FormSection>
          )
        }

        return (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.publicity}>
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={values.publicity}
                    onChange={set('publicity')}
                    options={[
                      { value: String(CATEGORY_PUBLICITY.PUBLIC), label: S.form.public },
                      { value: String(CATEGORY_PUBLICITY.PRIVATE), label: S.form.private },
                    ]}
                  />
                )}
              </FormRow>

              <FormSpan>
                <GroupPicker
                  options={visibleCategories}
                  selected={values.categoryIds}
                  onChange={set('categoryIds')}
                  legend={S.form.categories}
                  error={errors.categoryIds}
                  selectAllLabel={S.form.selectAll}
                  filterLabel={S.form.filter}
                />
              </FormSpan>

              {/* THE AUDIENCE. Without this the form could collect topics and nothing else,
                  so every create was refused by the server for want of a group. */}
              <FormSpan>
                <GroupPicker
                  options={groups}
                  selected={values.groupIds}
                  onChange={set('groupIds')}
                  legend={S.form.groups}
                  error={errors.groupIds}
                  selectAllLabel={S.form.selectAll}
                  filterLabel={S.form.filter}
                  emptyLabel={S.form.groupsEmpty}
                  /* Departments AND special groups — the switch keeps the second reachable
                     when the first runs to thousands. */
                  kinds={[
                    { id: 'departments', label: S.form.departments },
                    { id: 'groups', label: S.form.specialGroups },
                  ]}
                />
              </FormSpan>
            </FormCols>
          </FormSection>
        )
      }}
    </FormPage>
  )
}
